import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as ep from '@/api/endpoints';
import type { Address, Cart, Product } from '@/api/models';
import { qk } from './queries';

/** The shopper's cart, kept in sync with WooCommerce. Every mutation returns the full new cart. */
export function useCart() {
  return useQuery({ queryKey: qk.cart, queryFn: ep.fetchCart, staleTime: 30_000 });
}

export function useCartActions() {
  const qc = useQueryClient();
  const setCart = (c: Cart) => qc.setQueryData(qk.cart, c);
  const opts = { onSuccess: setCart };

  const add = useMutation({
    mutationFn: (v: { product: Product; size: string; quantity: number }) => ep.addItem(v.product, v.size, v.quantity),
    ...opts,
  });
  const update = useMutation({ mutationFn: (v: { key: string; quantity: number }) => ep.updateItem(v.key, v.quantity), ...opts });
  const remove = useMutation({ mutationFn: (key: string) => ep.removeItem(key), ...opts });
  const applyCoupon = useMutation({ mutationFn: (code: string) => ep.applyCoupon(code), ...opts });
  const removeCoupon = useMutation({ mutationFn: (code: string) => ep.removeCoupon(code), ...opts });
  const selectRate = useMutation({
    mutationFn: (v: { packageId: number | string; rateId: string }) => ep.selectShippingRate(v.packageId, v.rateId),
    ...opts,
  });
  const updateCustomer = useMutation({ mutationFn: (a: Address) => ep.updateCustomer(a), ...opts });

  return { add, update, remove, applyCoupon, removeCoupon, selectRate, updateCustomer };
}
